import { FC, useEffect } from 'react';

import { Box, BoxProps, FormLabel, MenuItem, Select } from '@mui/material';
import { useField } from 'formik';

import { statusColors } from '../NPIAutoComplete';

export type OptionTypes = {
    value: string | number;
    label: string;
    status?: string;
};

type DropDownProps = {
    name: string;
    options: OptionTypes[];
    label?: string;
    boxStyling?: BoxProps;
    disabled?: boolean;
    placeholder?: string;
    onChange?: (value: string | number) => void;
    dataTestId?: string;
    defaultValue?: string | number;
};

export const DropDown: FC<DropDownProps> = ({
    name,
    options,
    label,
    boxStyling,
    disabled = false,
    placeholder = 'Select a value',
    onChange,
    dataTestId = `${name}-dropdown`,
    defaultValue,
}) => {
    const [{ value }, { error, touched }, { setValue }] = useField(name);

    const hasError = error && touched;

    const borderColor = hasError ? 'red' : '#F39E4E';

    useEffect(() => {
        if (defaultValue) {
            setValue(defaultValue);
        }
    }, []);

    useEffect(() => {
        if (onChange) {
            onChange(value);
        }
    }, [value]);

    return (
        <Box display="flex" flexDirection="column" flex={0.49} {...boxStyling}>
            {label && (
                <FormLabel sx={{ marginBottom: '8px', color: 'grey.900', fontWeight: 600 }}>
                    {label}
                </FormLabel>
            )}

            <Select
                fullWidth
                sx={{
                    minWidth: '192px',
                    color: 'grey.900',
                    bgcolor: 'white',
                    '::placeholder': {
                        color: '#DADADA',
                    },
                    '&.MuiOutlinedInput-root': {
                        '& fieldset': {
                            borderColor,
                        },
                        '&:hover fieldset': {
                            borderColor,
                        },
                        '&.Mui-focused fieldset': {
                            borderColor,
                        },
                    },
                }}
                data-testid={dataTestId}
                defaultValue={defaultValue}
                value={value}
                onChange={(e) => {
                    return setValue(e.target.value);
                }}
                name={name}
                disabled={disabled}
                displayEmpty
                placeholder={placeholder}
                renderValue={(value) => {
                    // cannot use isEmpty here, since some options will have a value of 1, for instance a database value with an ID of 1
                    if (value === undefined || value === null || value === '') {
                        return <span style={{ opacity: 0.6 }}>{placeholder}</span>;
                    }
                    const selectedOption = options?.find((option) => {
                        return option?.value == value;
                    }) || { label: '' };
                    return selectedOption.label;
                }}
            >
                {options?.map((option) => {
                    return (
                        <MenuItem key={option?.value + option?.label} value={option?.value}>
                            {option?.label} {'  '}
                            {option?.status ? (
                                <span style={statusColors[option.status]}>{option.status}</span>
                            ) : (
                                ''
                            )}
                        </MenuItem>
                    );
                })}
            </Select>
            {hasError && <label className="error">{error}</label>}
        </Box>
    );
};
